// src/components/quizzes/DragDropQuiz.js
//
// DRAG & DROP — rebuilt (Nov 2026). Same data and the same result as before:
//   questions[] = { question, options: [{ id?, text?, image?, targetText?, targetImage? }] }
//   each option is a PAIR: the card (text / image) belongs in the box
//   (targetText / targetImage).
//   Round options (Game generator ▸ "Picture only"):
//     hideCardText  cards that have a picture show ONLY the picture
//     hideBoxText   boxes that have a picture show ONLY the picture
//   The words are still used for read-aloud, the "✔ answer" hint and the
//   teacher's answer list, so a picture-only round reports "Mountain", not
//   "[picture]".
//   onComplete({ score, details, answers })  — 5 points per right card;
//   details[q].pairs and answers[] in the same shape the Classroom reads.
//
// How it plays
//   * Cards sit in a tray; boxes below. DRAG a card with a finger, a mouse or
//     the smart-board pen — or TAP a card, then TAP its box. (The old version
//     used the browser's HTML drag-and-drop, which doesn't work on tablets or
//     smart boards at all.)
//   * Tap a card already in a box to pick it up again; tap the tray to put it
//     back.
//   * "Check" marks each box green or red and shows the right card for any
//     red one; "Next" moves on.
//   * 🔊 reads the question, the boxes and the cards aloud.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { playGameSound } from "../../services/gameSounds";
import { resolveImageSrc } from "../../utils/resolveImageSrc";
import { launchConfetti } from "../../utils/confetti";
import { sfx } from "./sfx";
import { QuizHud, useGameMusic, useReadAloud } from "./kit/QuizKit";

const MUSIC =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fdraganddrop.mp3?alt=media&token=fbfd52d1-f315-4948-91e2-f2202766d62f";
const DROP_SFX =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306";
const POINTS = 5;

const shuffle = (a) => {
  const x = [...a];
  for (let i = x.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [x[i], x[j]] = [x[j], x[i]];
  }
  return x;
};
const play = (url) => {
  try {
    const a = sfx(url);
    a.currentTime = 0;
    a.play().catch(() => {});
  } catch {
    /* ignore */
  }
};
const cardLabel = (o) => (o?.text && String(o.text).trim()) || (o?.image ? "[picture]" : "(no answer)");
const boxLabel = (o) => (o?.targetText && String(o.targetText).trim()) || (o?.text && String(o.text).trim()) || (o?.targetImage ? "[picture]" : "(unknown)");

// A real picture (AI, upload, link) rather than an emoji icon. Rounds with
// real pictures get bigger cards; emoji rounds look exactly as before.
const isRealPic = (src) => Boolean(src) && !String(src).startsWith("data:image/svg+xml");

const Card = ({ o, hideText = false, className = "", tone = 0, ...rest }) => {
  const picOnly = hideText && Boolean(o.image);
  return (
    <button
      type="button"
      className={`qk-chipcard is-c${tone % 6} ${picOnly ? "is-pic" : ""} ${className}`}
      data-card={o.id}
      aria-label={picOnly ? cardLabel(o) : undefined}
      {...rest}
    >
      {o.image && <img src={resolveImageSrc(o.image)} alt={picOnly ? cardLabel(o) : ""} draggable="false" />}
      {o.text && !picOnly && <span>{o.text}</span>}
    </button>
  );
};

const DragDropQuiz = ({ questions = [], onComplete, topic }) => {
  const [qi, setQi] = useState(0);
  const [score, setScore] = useState(0);
  const [place, setPlace] = useState({}); // boxId -> cardId
  const [picked, setPicked] = useState(null); // cardId (tap mode)
  const [drag, setDrag] = useState(null); // { id, x, y }
  const [over, setOver] = useState(null); // boxId under the finger
  const [checked, setChecked] = useState(false);
  const details = useRef([]);
  const press = useRef(null); // { id, x, y, moved }
  const [muted, toggleMute, duck] = useGameMusic(MUSIC);
  const voice = useReadAloud(duck);

  const q = questions[qi] || null;
  const opts = useMemo(() => (q?.options || []).filter(Boolean).map((o, n) => ({ ...o, id: String(o.id ?? `o${n}`) })), [q]);
  const order = useMemo(() => ({ cards: shuffle(opts.map((o) => o.id)), boxes: shuffle(opts.map((o) => o.id)) }), [opts]);
  const byId = useMemo(() => Object.fromEntries(opts.map((o) => [o.id, o])), [opts]);
  const hideCard = Boolean(q?.hideCardText);
  const hideBox = Boolean(q?.hideBoxText);
  const bigPics = opts.some((o) => isRealPic(o.image));

  useEffect(() => {
    setPlace({});
    setPicked(null);
    setChecked(false);
    voice.stop();
    // get this round's voice ready now, so 🔊 starts at once in the natural voice
    voice.preload([
      { key: "q", text: q?.question || "Match each card to its box." },
      ...opts.map((o) => ({ key: `b:${o.id}`, text: boxLabel(o) })),
      ...opts.map((o) => ({ key: `c:${o.id}`, text: cardLabel(o) })),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qi]);

  if (!q) return null;
  const placedIds = new Set(Object.values(place));
  const tray = order.cards.filter((id) => !placedIds.has(id));
  const allPlaced = opts.length > 0 && order.boxes.every((b) => place[b]);

  const putIn = (cardId, boxId) => {
    if (checked || !cardId) return;
    setPlace((p) => {
      const next = {};
      Object.entries(p).forEach(([b, c]) => {
        if (c !== cardId && b !== boxId) next[b] = c;
      });
      next[boxId] = cardId; // a card already in that box goes back to the tray
      return next;
    });
    setPicked(null);
    play(DROP_SFX);
  };
  const backToTray = (cardId) => {
    if (checked) return;
    setPlace((p) => Object.fromEntries(Object.entries(p).filter(([, c]) => c !== cardId)));
    setPicked(null);
  };

  // ---- pointer dragging (mouse, finger, pen) ----
  const boxAt = (x, y) => {
    const el = document.elementFromPoint(x, y);
    return el?.closest?.("[data-box]")?.getAttribute("data-box") || null;
  };
  const trayAt = (x, y) => Boolean(document.elementFromPoint(x, y)?.closest?.("[data-tray]"));
  const onDown = (e, id) => {
    if (checked) return;
    press.current = { id, x: e.clientX, y: e.clientY, moved: false };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };
  const onMove = (e) => {
    const p = press.current;
    if (!p) return;
    if (!p.moved && Math.hypot(e.clientX - p.x, e.clientY - p.y) < 8) return;
    p.moved = true;
    setDrag({ id: p.id, x: e.clientX, y: e.clientY });
    setOver(boxAt(e.clientX, e.clientY));
  };
  const onUp = (e) => {
    const p = press.current;
    press.current = null;
    setDrag(null);
    setOver(null);
    if (!p) return;
    if (p.moved) {
      const b = boxAt(e.clientX, e.clientY);
      if (b) putIn(p.id, b);
      else if (trayAt(e.clientX, e.clientY)) backToTray(p.id);
      return;
    }
    // a tap: pick it up / put it down
    setPicked((cur) => (cur === p.id ? null : p.id));
  };
  const cardHandlers = (id) => ({
    onPointerDown: (e) => onDown(e, id),
    onPointerMove: onMove,
    onPointerUp: onUp,
    onPointerCancel: () => {
      press.current = null;
      setDrag(null);
      setOver(null);
    },
  });

  const check = () => {
    voice.stop();
    const pairs = order.boxes.map((boxId, idx) => {
      const got = place[boxId] || null;
      return {
        pairIndex: idx,
        targetId: boxId,
        targetLabel: boxLabel(byId[boxId]),
        studentId: got,
        studentLabel: cardLabel(byId[got]),
        correctId: boxId,
        correctLabel: cardLabel(byId[boxId]),
        isCorrect: got === boxId,
      };
    });
    const right = pairs.filter((x) => x.isCorrect).length;
    details.current[qi] = { qIndex: qi, question: q.question, pairs };
    setScore((s) => s + right * POINTS);
    setChecked(true);
    // all right = the "right answer" sound, any wrong = the "wrong answer"
    // sound (Layout Builder → 🎵 Game sounds); built-in: the drop click
    playGameSound(right === pairs.length ? "correct" : "wrong", "dragDrop", () => play(DROP_SFX));
    if (right === pairs.length) launchConfetti();
  };

  const next = () => {
    if (qi + 1 < questions.length) {
      setQi(qi + 1);
      return;
    }
    const list = details.current.filter(Boolean);
    const final = list.reduce((n, r) => n + r.pairs.filter((x) => x.isCorrect).length * POINTS, 0);
    const answers = list.flatMap((r) =>
      r.pairs.map((x) => ({
        question: r.question ? `${r.question} — ${x.targetLabel}` : `Match: ${x.targetLabel}`,
        selectedText: x.studentLabel || "(no answer)",
        correctText: x.correctLabel || "(unknown)",
        correct: Boolean(x.isCorrect),
      }))
    );
    onComplete?.({ score: final, details: list, answers });
  };

  // read aloud: question, then each box, then each card in the tray
  const parts = [
    { key: "q", text: q.question || "Match each card to its box." },
    ...order.boxes.map((b) => ({ key: `b:${b}`, text: boxLabel(byId[b]) })),
    ...tray.map((c) => ({ key: `c:${c}`, text: cardLabel(byId[c]) })),
  ];
  const reading = voice.partIndex === null ? null : parts[voice.partIndex]?.key;
  const dragged = drag ? byId[drag.id] : null;

  return (
    <div className="qk-scene" onPointerMove={drag ? onMove : undefined}>
      <div className="qk-wrap">
        <QuizHud title={topic?.title || ""} index={qi} total={questions.length} score={score} muted={muted} onMute={toggleMute} />

        <section className={`qk-question ${reading === "q" ? "is-reading" : ""}`} key={`q${qi}`}>
          <h2 className="qk-question-text">{q.question || "Match each card to its box!"}</h2>
          <span className="qk-read-hint">Drag each card into its box — or tap a card, then tap the box.</span>
          {voice.can && (
            <button type="button" className={`qk-read ${voice.reading ? "is-on" : ""}`} onClick={() => (voice.reading ? voice.stop() : voice.read(parts))} aria-label={voice.reading ? "Stop reading" : "Read it to me"}>
              {voice.reading ? "■" : "🔊"}
            </button>
          )}
        </section>

        <div className={`qk-dd ${bigPics ? "has-pics" : ""}`}>
          <p className="qk-dd-label">Cards</p>
          <div
            className="qk-tray"
            data-tray
            onClick={(e) => {
              if (picked && placedIds.has(picked) && e.target === e.currentTarget) backToTray(picked);
            }}
          >
            {tray.length === 0 ? (
              <span className="qk-tray-empty">{checked ? "" : "All cards placed — press Check!"}</span>
            ) : (
              tray.map((id) => (
                <Card
                  key={id}
                  o={byId[id]}
                  hideText={hideCard}
                  tone={order.cards.indexOf(id)}
                  className={`${picked === id ? "is-picked" : ""} ${drag?.id === id ? "is-ghost" : ""} ${reading === `c:${id}` ? "is-reading" : ""}`}
                  {...cardHandlers(id)}
                />
              ))
            )}
          </div>

          <div className={`qk-targets ${order.boxes.some((b) => byId[b]?.targetImage) ? "has-img" : ""}`}>
            {order.boxes.map((b) => {
              const o = byId[b];
              const inBox = place[b];
              const state = checked ? (inBox === b ? "is-right" : "is-wrong") : over === b ? "is-over" : picked && !inBox ? "is-ready" : "";
              return (
                <div
                  key={b}
                  className={`qk-target ${state} ${reading === `b:${b}` ? "is-reading" : ""}`}
                  data-box={b}
                  onClick={() => {
                    if (picked && picked !== inBox) putIn(picked, b);
                  }}
                >
                  {o.targetImage && (
                    <img
                      className={`qk-target-img ${hideBox ? "is-pic" : ""}`}
                      src={resolveImageSrc(o.targetImage)}
                      alt={hideBox ? boxLabel(o) : ""}
                      draggable="false"
                    />
                  )}
                  {(!o.targetImage || (!hideBox && o.targetText)) && <span className="qk-target-title">{boxLabel(o)}</span>}
                  <div className="qk-slot">
                    {inBox ? (
                      <Card
                        o={byId[inBox]}
                        hideText={hideCard}
                        tone={order.cards.indexOf(inBox)}
                        className={`${picked === inBox ? "is-picked" : ""} ${drag?.id === inBox ? "is-ghost" : ""}`}
                        {...cardHandlers(inBox)}
                      />
                    ) : (
                      "Drop a card here"
                    )}
                  </div>
                  {checked && inBox !== b && (
                    <span className="qk-correct-was">
                      ✔ {o.image && hideCard && <img className="qk-correct-img" src={resolveImageSrc(o.image)} alt="" draggable="false" />}
                      {cardLabel(o)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="qk-actions">
            {!checked ? (
              <button type="button" className="qk-btn" onClick={check} disabled={!allPlaced}>
                ✓ Check
              </button>
            ) : (
              <button type="button" className="qk-btn" onClick={next}>
                {qi + 1 < questions.length ? "Next ➜" : "Finish 🎉"}
              </button>
            )}
            {!checked && Object.keys(place).length > 0 && (
              <button type="button" className="qk-btn is-soft" onClick={() => setPlace({})}>
                ↺ Start over
              </button>
            )}
          </div>
          {!checked && !allPlaced && <p className="qk-hint">Put every card in a box, then press Check.</p>}
        </div>
      </div>

      {dragged && (
        <div className="qk-drag-ghost" style={{ left: drag.x, top: drag.y }}>
          <Card o={dragged} hideText={hideCard} tone={order.cards.indexOf(dragged.id)} tabIndex={-1} />
        </div>
      )}
    </div>
  );
};

export default DragDropQuiz;
